<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue'
import { GameAssetTypes, type Game } from '@game/types'

const links = useLinks()

const HERO_SLUGS = [
    'monopoly-big-baller',
    'big-bass-splash',
    'lightning-roulette',
    'crazy-time-live',
    'ice-fishing'
]

interface ArcSlot {
    slug: string
    game: Game | undefined
}

const { games: fetchedGames } = useGames({
    params: { page: 1, page_size: HERO_SLUGS.length },
    filter: { slugs: HERO_SLUGS },
    sort: 'KEEP_ORDER',
    key: 'lux-hero-arc',
})

const { assetUrl, videoAssetUrl, hasAsset, loadVideo } = useGameAssets()
const { start: startGame } = useGame()

const slots = computed<ArcSlot[]>(() => {
    const bySlug = new Map(fetchedGames.value.map(g => [g.slug, g]))
    return HERO_SLUGS.map(slug => ({ slug, game: bySlug.get(slug) }))
})

function humanizeSlug(slug: string) {
    return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

function displayName(slot: ArcSlot) {
    return slot.game?.name ?? humanizeSlug(slot.slug)
}

const centerHasVideo = computed(() => {
    const center = slots.value[centerIdx.value]
    return !!(center?.game && hasAsset(center.game, GameAssetTypes.HERO_VIDEO))
})

async function startCenterGame() {
    const center = slots.value[centerIdx.value]
    if (!center?.game) return
    await startGame({
        gameSlug: center.game.slug,
        gameInstanceId: center.game.instance_id,
        providerSlug: center.game.provider_slug,
        game: center.game,
    })
}

// Arc position keyframes: [leftOffset, width, rotateY, translateX, scale, brightness, borderOpacity, zIndex]
const arcKeysDesktop = [
    [-640, 330, 55, 60, 0.78, 0.78, 0.25, 1],
    [-470, 350, 32, 24, 0.88, 0.85, 0.45, 3],
    [-260, 520, 0, 0, 1.00, 1.00, 1.00, 5],
    [140, 350, -32, -24, 0.88, 0.85, 0.45, 3],
    [320, 330, -55, -60, 0.78, 0.78, 0.25, 1],
] as const

const arcKeysMobile = [
    [-270, 190, 55, 31, 0.78, 0.88, 0.25, 1],
    [-190, 205, 32, 12, 0.88, 0.92, 0.45, 3],
    [-150, 300, 0, 0, 1.00, 1.00, 1.00, 5],
    [-10, 205, -32, -12, 0.88, 0.92, 0.45, 3],
    [85, 190, -55, -31, 0.78, 0.88, 0.25, 1],
] as const

type ArcKeyArray = typeof arcKeysDesktop | typeof arcKeysMobile

interface ArcProps {
    left: number
    width: number
    rotateY: number
    translateX: number
    scale: number
    brightness: number
    borderOpacity: number
    zIndex: number
}

const arcRef = ref<HTMLElement | null>(null)
const cardRefs = ref<HTMLElement[]>([])

// positions[i] is the arc slot (0..4) currently occupied by card i.
// Slot 2 is the centered card. Lifted to setup-scope so `centerIdx` can
// drive the template (e.g. `v-if="index === centerIdx"` for the iframe).
const positions = ref<number[]>([0, 1, 2, 3, 4])
const centerIdx = computed(() => positions.value.indexOf(2))

const reducedMotion = usePreferredReducedMotion()
const isMobileArc = useMediaQuery('(max-width: 639px)')

function setCardRef(el: Element | ComponentPublicInstance | null, index: number) {
    if (el instanceof HTMLElement) {
        cardRefs.value[index] = el
    }
}

onMounted(() => {
    const arc = arcRef.value
    if (!arc) return
    const cards = cardRefs.value
    if (cards.length !== 5) return

    // Hide cards initially — JS owns visibility
    cards.forEach((card) => {
        card.style.opacity = '0'
    })

    let arcKeys: ArcKeyArray = isMobileArc.value ? arcKeysMobile : arcKeysDesktop

    function propsFromKey(i: number): ArcProps {
        const k = arcKeys[i]!
        return {
            left: k[0], width: k[1], rotateY: k[2], translateX: k[3],
            scale: k[4], brightness: k[5], borderOpacity: k[6], zIndex: k[7],
        }
    }

    function getArcProps(t: number): ArcProps {
        if (t <= 0) return propsFromKey(0)
        if (t >= 4) return propsFromKey(4)
        const lo = Math.floor(t)
        const hi = Math.ceil(t)
        if (lo === hi) return propsFromKey(lo)
        const frac = t - lo
        const a = arcKeys[lo]!
        const b = arcKeys[hi]!
        return {
            left: a[0] + (b[0] - a[0]) * frac,
            width: a[1] + (b[1] - a[1]) * frac,
            rotateY: a[2] + (b[2] - a[2]) * frac,
            translateX: a[3] + (b[3] - a[3]) * frac,
            scale: a[4] + (b[4] - a[4]) * frac,
            brightness: a[5] + (b[5] - a[5]) * frac,
            borderOpacity: a[6] + (b[6] - a[6]) * frac,
            zIndex: Math.round(a[7] + (b[7] - a[7]) * frac),
        }
    }

    function applyProps(card: HTMLElement, p: ArcProps) {
        card.style.left = `calc(50% + ${p.left}px)`
        card.style.width = `${p.width}px`
        card.style.transform = `translateY(-50%) rotateY(${p.rotateY}deg) translateX(${p.translateX}px) scale(${p.scale})`
        card.style.filter = `brightness(${p.brightness})`
        card.style.zIndex = String(p.zIndex)
        if (p.borderOpacity >= 1) {
            card.style.borderColor = '#f5d040'
            card.style.borderWidth = '1.5px'
        }
        else {
            card.style.borderColor = `rgba(201,158,0,${p.borderOpacity})`
            card.style.borderWidth = '1px'
        }
        if (p.zIndex >= 5) {
            card.style.boxShadow = '0 8px 32px rgba(0,0,0,0.6), 0 0 40px rgba(245,208,64,0.18)'
        }
        else {
            card.style.boxShadow = '0 8px 32px rgba(0,0,0,0.6), 0 0 20px rgba(201,152,76,0.12)'
        }
    }

    function applyClasses() {
        cards.forEach((card, i) => {
            card.classList.remove('arc-pos-1', 'arc-pos-2', 'arc-pos-3', 'arc-pos-4', 'arc-pos-5')
            card.classList.add(`arc-pos-${positions.value[i]! + 1}`)
            card.style.left = ''
            card.style.width = ''
            card.style.transform = ''
            card.style.filter = ''
            card.style.zIndex = ''
            card.style.borderColor = ''
            card.style.borderWidth = ''
            card.style.boxShadow = ''
            card.style.opacity = '1'
        })
    }

    // Easing: cubic-bezier solver (Newton-Raphson + bisection)
    function solveCubicBezierX(x: number, x1: number, x2: number): number {
        let t = x
        for (let i = 0; i < 8; i++) {
            const mt = 1 - t
            const cx = 3 * mt * mt * t * x1 + 3 * mt * t * t * x2 + t * t * t - x
            if (Math.abs(cx) < 1e-6) return t
            const dx = 3 * (1 - t) * (1 - t) * x1 + 6 * (1 - t) * t * (x2 - x1) + 3 * t * t * (1 - x2)
            if (Math.abs(dx) < 1e-6) break
            t -= cx / dx
        }
        let lo = 0
        let hi = 1
        t = x
        for (let j = 0; j < 20; j++) {
            const mt2 = 1 - t
            const cx2 = 3 * mt2 * mt2 * t * x1 + 3 * mt2 * t * t * x2 + t * t * t
            if (Math.abs(cx2 - x) < 1e-6) return t
            if (cx2 < x) {
                lo = t
            }
            else {
                hi = t
            }
            t = (lo + hi) / 2
        }
        return t
    }

    function bezierY(t: number, y1: number, y2: number): number {
        const mt = 1 - t
        return 3 * mt * mt * t * y1 + 3 * mt * t * t * y2 + t * t * t
    }

    function easingStandard(t: number): number {
        return bezierY(solveCubicBezierX(t, 0.4, 0.2), 0.0, 1.0)
    }

    function easingFanIn(t: number): number {
        return bezierY(solveCubicBezierX(t, 0.22, 0.36), 1.0, 1.0)
    }

    function lerpProps(a: ArcProps, b: ArcProps, t: number): ArcProps {
        return {
            left: a.left + (b.left - a.left) * t,
            width: a.width + (b.width - a.width) * t,
            rotateY: a.rotateY + (b.rotateY - a.rotateY) * t,
            translateX: a.translateX + (b.translateX - a.translateX) * t,
            scale: a.scale + (b.scale - a.scale) * t,
            brightness: a.brightness + (b.brightness - a.brightness) * t,
            borderOpacity: a.borderOpacity + (b.borderOpacity - a.borderOpacity) * t,
            zIndex: Math.round(a.zIndex + (b.zIndex - a.zIndex) * t),
        }
    }

    function fanInStartForPos(targetPos: number): ArcProps {
        const target = propsFromKey(targetPos)
        const center = propsFromKey(2)
        const s = 0.08
        return {
            left: center.left + (target.left - center.left) * s,
            width: center.width + (target.width - center.width) * s,
            rotateY: center.rotateY + (target.rotateY - center.rotateY) * s,
            translateX: center.translateX + (target.translateX - center.translateX) * s,
            scale: 0.72,
            brightness: center.brightness + (target.brightness - center.brightness) * s,
            borderOpacity: center.borderOpacity + (target.borderOpacity - center.borderOpacity) * s,
            zIndex: target.zIndex,
        }
    }

    // State (positions is hoisted to setup-scope above so the template can
    // derive `centerIdx` from it).
    let animating = false
    let animFrameId: number | null = null
    let introTimers: ReturnType<typeof setTimeout>[] = []
    let introFrameIds: number[] = []
    let introComplete = false

    const FAN_IN_DELAY = 600
    const FAN_IN_DURATION = 650
    const FAN_IN_STAGGER = [120, 60, 0, 60, 120]
    const FAN_IN_MAX_STAGGER = 120

    function runFanIn(onComplete?: () => void) {
        arcKeys = isMobileArc.value ? arcKeysMobile : arcKeysDesktop

        if (reducedMotion.value === 'reduce') {
            cards.forEach((card) => {
                card.style.opacity = '1'
            })
            applyClasses()
            introComplete = true
            onComplete?.()
            return
        }

        let cardsFinished = 0

        cards.forEach((card, i) => {
            const targetPos = positions.value[i]!
            const targetProps = propsFromKey(targetPos)
            const startProps = fanInStartForPos(targetPos)
            const stagger = FAN_IN_STAGGER[targetPos]!
            const delay = FAN_IN_DELAY + stagger
            const duration = FAN_IN_DURATION + FAN_IN_MAX_STAGGER - stagger
            const FADE_IN_MS = 120

            const timerId = setTimeout(() => {
                let startTime: number | null = null

                function tick(now: number) {
                    if (!startTime) startTime = now
                    const elapsed = now - startTime
                    const rawT = Math.min(elapsed / duration, 1)
                    const easedT = easingFanIn(rawT)
                    const props = lerpProps(startProps, targetProps, easedT)
                    applyProps(card, props)

                    if (elapsed < FADE_IN_MS) {
                        card.style.opacity = String(Math.min(elapsed / FADE_IN_MS, 1))
                    }
                    else {
                        card.style.opacity = '1'
                    }

                    if (rawT < 1) {
                        const fid = requestAnimationFrame(tick)
                        introFrameIds.push(fid)
                    }
                    else {
                        applyProps(card, targetProps)
                        card.classList.add(`arc-pos-${targetPos + 1}`)
                        cardsFinished++
                        if (cardsFinished === 5) {
                            introComplete = true
                            onComplete?.()
                        }
                    }
                }

                const fid = requestAnimationFrame(tick)
                introFrameIds.push(fid)
            }, delay)

            introTimers.push(timerId)
        })
    }

    function cancelIntro() {
        introTimers.forEach(id => clearTimeout(id))
        introTimers = []
        introFrameIds.forEach(id => cancelAnimationFrame(id))
        introFrameIds = []
    }

    const DURATION = 800

    function rotateCards(direction: number, onComplete?: () => void) {
        if (animating) return
        arcKeys = isMobileArc.value ? arcKeysMobile : arcKeysDesktop

        const oldPositions = positions.value.slice()
        if (direction > 0) {
            positions.value = positions.value.map(p => (p + 1) % 5)
        }
        else {
            positions.value = positions.value.map(p => (p + 4) % 5)
        }

        let wrapIndex = -1
        for (let i = 0; i < 5; i++) {
            if (direction > 0 && oldPositions[i] === 4) {
                wrapIndex = i
                break
            }
            if (direction < 0 && oldPositions[i] === 0) {
                wrapIndex = i
                break
            }
        }

        animating = true
        arc.classList.add('lux-arc-rotating')
        clearSpread()
        let startTime: number | null = null

        const FADE_OUT_END = 0.30
        const FADE_IN_START = 0.70

        function tick(now: number) {
            if (!startTime) startTime = now
            const elapsed = now - startTime
            const rawT = Math.min(elapsed / DURATION, 1)
            const easedT = easingStandard(rawT)

            for (let i = 0; i < 5; i++) {
                const from = oldPositions[i]!
                const to = positions.value[i]!

                if (i === wrapIndex) {
                    const exitPos = direction > 0 ? 4 : 0
                    const enterPos = direction > 0 ? 0 : 4
                    if (rawT < FADE_OUT_END) {
                        const fadeOutT = rawT / FADE_OUT_END
                        applyProps(cards[i]!, propsFromKey(exitPos))
                        cards[i]!.style.opacity = String(1 - fadeOutT)
                    }
                    else if (rawT < FADE_IN_START) {
                        applyProps(cards[i]!, propsFromKey(enterPos))
                        cards[i]!.style.opacity = '0'
                    }
                    else {
                        const fadeInT = (rawT - FADE_IN_START) / (1 - FADE_IN_START)
                        applyProps(cards[i]!, propsFromKey(enterPos))
                        cards[i]!.style.opacity = String(fadeInT)
                    }
                }
                else {
                    const currentPos = from + (to - from) * easedT
                    applyProps(cards[i]!, getArcProps(currentPos))
                    if (from === 2) {
                        const yieldOpacity = 1 - 0.15 * 4 * rawT * (1 - rawT)
                        cards[i]!.style.opacity = String(yieldOpacity)
                    }
                    else {
                        cards[i]!.style.opacity = '1'
                    }
                }
            }

            if (rawT < 1) {
                animFrameId = requestAnimationFrame(tick)
            }
            else {
                animating = false
                animFrameId = null
                // Settle inline styles and arc-pos classes first, then drop the
                // rotating flag last. Reverse order would briefly enable CSS
                // transitions and the !important hover transform while the
                // arc-pos class is still stale, snapping the just-rotated card
                // to the wrong hover-state pose for a frame.
                for (let j = 0; j < 5; j++) {
                    applyProps(cards[j]!, propsFromKey(positions.value[j]!))
                    cards[j]!.style.opacity = '1'
                    cards[j]!.classList.remove('arc-pos-1', 'arc-pos-2', 'arc-pos-3', 'arc-pos-4', 'arc-pos-5')
                    cards[j]!.classList.add(`arc-pos-${positions.value[j]! + 1}`)
                }
                arc.classList.remove('lux-arc-rotating')
                onComplete?.()
            }
        }

        animFrameId = requestAnimationFrame(tick)
    }

    function navigateToCard(clickedIdx: number) {
        if (animating || !introComplete) return
        const clickedPos = positions.value[clickedIdx]!
        if (clickedPos === 2) return

        if (isMobileArc.value) {
            rotateCards(clickedPos < 2 ? 1 : -1)
            return
        }

        const stepsForward = (2 - clickedPos + 5) % 5
        const stepsBackward = (clickedPos - 2 + 5) % 5
        const dir = stepsForward <= stepsBackward ? 1 : -1
        let remaining = Math.min(stepsForward, stepsBackward)

        function step() {
            if (remaining <= 0) return
            remaining--
            rotateCards(dir, step)
        }
        step()
    }

    // Hover spread + tilt
    const SPREAD_PER_STEP_DESKTOP = 12
    const SPREAD_PER_STEP_MOBILE = 7
    const TILT_ADJACENT = 5
    const TILT_FAR = 3
    let spreadActive = false

    function clearSpread() {
        if (!spreadActive) return
        spreadActive = false
        cards.forEach((card, i) => {
            applyProps(card, propsFromKey(positions.value[i]!))
            card.style.opacity = '1'
        })
    }

    function applySpread(hoveredIndex: number) {
        if (animating || !introComplete) return
        arcKeys = isMobileArc.value ? arcKeysMobile : arcKeysDesktop
        const hoveredPos = positions.value[hoveredIndex]!
        spreadActive = true

        cards.forEach((card, i) => {
            const cardPos = positions.value[i]!
            const distance = cardPos - hoveredPos
            const absDist = Math.abs(distance)
            if (distance === 0) return

            const spreadStep = isMobileArc.value ? SPREAD_PER_STEP_MOBILE : SPREAD_PER_STEP_DESKTOP
            const spreadPx = distance * spreadStep
            const baseLeft = arcKeys[cardPos]![0]
            card.style.left = `calc(50% + ${baseLeft + spreadPx}px)`

            let tiltDeg = 0
            if (absDist === 1) tiltDeg = TILT_ADJACENT
            else if (absDist === 2) tiltDeg = TILT_FAR
            const tiltSign = distance < 0 ? 1 : -1
            const extraRotateY = tiltSign * tiltDeg

            const baseRotateY = arcKeys[cardPos]![2]
            const baseTranslateX = arcKeys[cardPos]![3]
            const baseScale = arcKeys[cardPos]![4]
            card.style.transform = `translateY(-50%) rotateY(${baseRotateY + extraRotateY}deg) translateX(${baseTranslateX}px) scale(${baseScale})`
        })
    }

    // Bind events
    cards.forEach((card, i) => {
        card.addEventListener('click', () => navigateToCard(i))
        card.style.cursor = 'pointer'

        card.addEventListener('mouseenter', () => applySpread(i))
        card.addEventListener('mouseleave', () => clearSpread())
    })

    // Mobile arc container tap
    arc.addEventListener('click', (e: Event) => {
        if (!isMobileArc.value || animating || !introComplete) return
        if ((e.target as Element).closest('.lux-arc-card')) return
        const rect = arc.getBoundingClientRect()
        const x = (e as MouseEvent).clientX - rect.left
        if (x < rect.width / 2) {
            rotateCards(-1)
        }
        else {
            rotateCards(1)
        }
    })

    // Auto-rotate. useIntervalFn auto-cleans on unmount; pause()+resume()
    // restarts the timer fresh (used to defer the next rotation after user
    // interaction). `immediate: false` keeps it idle until fan-in finishes.
    const autoRotate = useIntervalFn(() => {
        if (!animating && introComplete) {
            rotateCards(-1)
        }
    }, 10000, { immediate: false })

    function resetAutoRotate() {
        if (reducedMotion.value === 'reduce') return
        autoRotate.pause()
        autoRotate.resume()
    }

    // Patch card interactions to reset the auto-rotate timer
    cards.forEach((card, i) => {
        card.addEventListener('click', resetAutoRotate)
        card.addEventListener('mouseenter', resetAutoRotate)
    })
    arc.addEventListener('click', resetAutoRotate)

    // Boot
    runFanIn(() => {
        if (reducedMotion.value !== 'reduce') autoRotate.resume()
    })

    onBeforeUnmount(() => {
        if (animFrameId !== null) cancelAnimationFrame(animFrameId)
        cancelIntro()
        clearSpread()
    })
})
</script>

<template>
    <section class="lux-hero">
        <!-- Gold confetti particles -->
        <div class="lux-confetti">
            <i /><i /><i /><i /><i /><i />
        </div>

        <div class="hero-inner">
            <div class="hero-content">
                <div class="lux-hero-rule" />
                <div class="lux-hero-label">
                    Members only
                </div>

                <h1 class="brand-heading">
                    The Finest Slots.<br>Obviously.
                </h1>

                <div class="lux-hero-subcopy">
                    Membership is complimentary. The experience is not.
                </div>

                <NuxtLink
                    class="lux-btn-cta"
                    v-bind="links.signup.props()"
                >
                    <span>Claim Your Seat</span>
                </NuxtLink>

                <div class="lux-hero-trust">
                    <span>Curated Collection. Members Only. UKGC Licensed.</span>
                </div>
            </div>
        </div>

        <!-- Art Deco gold divider -->
        <div class="lux-arc-divider" />

        <!-- 5-card 3D arc -->
        <div
            ref="arcRef"
            class="lux-card-arc"
        >
            <div
                v-for="(slot, index) in slots"
                :key="slot.slug"
                :ref="(el) => setCardRef(el, index)"
                class="lux-arc-card"
                :class="{
                    'lux-arc-card--center': index === centerIdx,
                    'lux-arc-card--center-has-video': index === centerIdx && centerHasVideo,
                }"
                :style="slot.game?.game_hero_colour ? { '--lux-card-fallback-bg': slot.game.game_hero_colour } : undefined"
            >
                <NuxtImg
                    v-if="slot.game && hasAsset(slot.game, GameAssetTypes.TILE)"
                    class="lux-card-img"
                    provider="cloudflare"
                    preset="gameservice"
                    :src="assetUrl(slot.game, GameAssetTypes.TILE)"
                    :width="520"
                    :height="520"
                    fit="cover"
                    densities="1x 2x"
                    :alt="displayName(slot)"
                    decoding="async"
                    :loading="index === centerIdx ? 'eager' : 'lazy'"
                />
                <div
                    v-else
                    class="lux-card-img lux-card-fallback"
                    :aria-label="displayName(slot)"
                >
                    <span class="lux-card-fallback-name">{{ displayName(slot) }}</span>
                </div>
                <video
                    v-if="loadVideo && index === centerIdx && slot.game && hasAsset(slot.game, GameAssetTypes.HERO_VIDEO)"
                    class="lux-card-video"
                    preload="metadata"
                    autoplay
                    muted
                    loop
                    playsinline
                    disableremoteplayback
                    :aria-label="displayName(slot)"
                >
                    <source
                        :src="videoAssetUrl(slot.game, GameAssetTypes.HERO_VIDEO, 720, 720)"
                        type="video/mp4"
                    >
                </video>
                <div class="lux-card-vignette" />
                <NuxtImg
                    v-if="slot.game && hasAsset(slot.game, GameAssetTypes.LOGO)"
                    class="lux-card-logo"
                    provider="cloudflare"
                    preset="gameservice"
                    :src="assetUrl(slot.game, GameAssetTypes.LOGO)"
                    :width="160"
                    :height="80"
                    fit="contain"
                    densities="1x 2x"
                    alt=""
                    decoding="async"
                    loading="lazy"
                />
                <Transition name="lux-btn-fade">
                    <button
                        v-if="index === centerIdx && slot.game"
                        type="button"
                        class="lux-card-start"
                        @click.stop="startCenterGame"
                    >
                        Start game
                    </button>
                </Transition>
            </div>
        </div>
    </section>
</template>

<style scoped lang="scss">
.lux-hero {
    background-color: #0d2018;
    background-image: radial-gradient(ellipse 80% 60% at 50% 30%, rgba(240, 188, 0, 0.035) 0%, transparent 60%);
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    overflow: hidden;
    text-align: center;
    padding: 0;
    padding-top: 0;

    // Lifestyle people background
    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-image: url('/media/landing-page/lux/lux-hero-desktop.png');
        background-size: cover;
        background-position: left top;
        background-repeat: no-repeat;
        opacity: 0.09;
        z-index: 0;
        pointer-events: none;
    }

    // Art Deco sunburst
    &::before {
        content: '';
        position: absolute;
        top: 18%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 700px;
        height: 700px;
        background-image: repeating-conic-gradient(
            from 0deg,
            rgba(240, 188, 0, 0.04) 0deg,
            transparent 5deg,
            transparent 10deg
        );
        mask-image: radial-gradient(circle, black 15%, transparent 65%);
        -webkit-mask-image: radial-gradient(circle, black 15%, transparent 65%);
        pointer-events: none;
        z-index: 1;
    }
}

.hero-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 1200px;
    width: 100%;
    margin: 0 auto;
    padding: 148px 32px 0;
    position: relative;
    z-index: 2;
}

.hero-content {
    text-align: center;
    width: 100%;
}

.lux-hero-rule {
    width: 60px;
    height: 1px;
    background: #f0bc00;
    margin: 0 auto 12px;
    opacity: 0;
    animation: luxFadeIn 400ms ease 200ms forwards;
}

.lux-hero-label {
    font-family: 'Jost', sans-serif;
    font-weight: 400;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.35em;
    color: #f0bc00;
    margin-bottom: 28px;
    opacity: 0;
    animation: luxFadeIn 400ms ease 400ms forwards;
}

h1 {
    font-size: clamp(3rem, 5vw, 4.5rem);
    font-weight: 900;
    color: #ffffff;
    letter-spacing: 0.02em;
    line-height: 1.1;
    text-shadow: 0 0 40px rgba(255, 255, 255, 0.15);
    margin-bottom: 20px;
    text-align: center;
    opacity: 0;
    animation: luxSlideUp 600ms ease 600ms forwards;
}

.lux-hero-subcopy {
    font-family: 'Jost', sans-serif;
    font-weight: 300;
    font-size: 20px;
    color: rgba(255, 255, 255, 0.65);
    margin-bottom: 80px;
    line-height: 1.5;
    opacity: 0;
    animation: luxFadeIn 400ms ease 800ms forwards;
}

.lux-btn-cta {
    display: inline-block;
    background: linear-gradient(135deg, #f5d040, #f0bc00, #c99e00);
    color: #0d1a0f;
    border: none;
    border-bottom: 2px solid #c99e00;
    border-radius: 0;
    padding: 24px 108px;
    font-family: 'Jost', sans-serif;
    font-weight: 900;
    font-size: 27px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    white-space: nowrap;
    text-decoration: none;
    transition:
        background 0.3s ease,
        box-shadow 0.3s ease;
    margin: 0 auto;
    opacity: 0;
    animation: luxFadeIn 400ms ease 1000ms forwards;
    position: relative;
    overflow: hidden;

    span {
        background: none;
        padding: 0;
        border-radius: 0;
    }

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -75%;
        width: 50%;
        height: 100%;
        background: linear-gradient(120deg, transparent 0%, rgba(255, 255, 255, 0.28) 50%, transparent 100%);
        transform: skewX(-20deg);
        transition: left 0.45s ease;
        pointer-events: none;
    }

    &:hover::before {
        left: 130%;
    }

    &:hover {
        background: linear-gradient(135deg, #fde668, #f5d040, #d4a800);
        box-shadow:
            0 0 20px rgba(245, 208, 64, 0.6),
            0 0 48px rgba(245, 208, 64, 0.2),
            0 4px 16px rgba(0, 0, 0, 0.5);
        color: #0d1a0f;
    }
}

.lux-hero-trust {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 16px;
    font-family: 'Jost', sans-serif;
    font-weight: 400;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.4);
    letter-spacing: 0.06em;
    font-style: italic;
    opacity: 0;
    animation: luxFadeIn 400ms ease 1000ms forwards;
}

.lux-arc-divider {
    width: 60%;
    max-width: 500px;
    height: 1px;
    background: #c99e00;
    margin: 40px auto 0;
    transform: scaleX(0);
    animation: luxDrawLine 800ms ease 1200ms forwards;
}

// Gold confetti
.lux-confetti {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 3;
    overflow: hidden;

    i {
        position: absolute;
        display: block;
        background: #f0bc00;
        opacity: 0;
        animation: luxConfettiFall linear infinite;

        &:nth-child(1) {
            width: 3px;
            height: 3px;
            left: 12%;
            top: -5%;
            animation-duration: 12s;
            animation-delay: 0s;
        }
        &:nth-child(2) {
            width: 2px;
            height: 6px;
            left: 35%;
            top: -5%;
            animation-duration: 14s;
            animation-delay: 2s;
        }
        &:nth-child(3) {
            width: 4px;
            height: 2px;
            left: 58%;
            top: -5%;
            animation-duration: 10s;
            animation-delay: 4s;
        }
        &:nth-child(4) {
            width: 2px;
            height: 4px;
            left: 78%;
            top: -5%;
            animation-duration: 16s;
            animation-delay: 1s;
        }
        &:nth-child(5) {
            width: 3px;
            height: 2px;
            left: 92%;
            top: -5%;
            animation-duration: 11s;
            animation-delay: 6s;
        }
        &:nth-child(6) {
            width: 2px;
            height: 3px;
            left: 48%;
            top: -5%;
            animation-duration: 13s;
            animation-delay: 3s;
        }
    }
}

// Arc container
.lux-card-arc {
    padding: 32px 0 64px;
    position: relative;
    z-index: 2;
    width: 100%;
    height: 380px;
    perspective: 1200px;
    perspective-origin: 50% 50%;

    // Edge vignette
    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background:
            linear-gradient(to right, #0d2018 0%, transparent 18%, transparent 82%, #0d2018 100%),
            linear-gradient(to bottom, transparent 0%, rgba(10, 26, 18, 0.4) 92%, #0a1a12 100%);
        pointer-events: none;
        z-index: 10;
    }

    &.lux-arc-rotating .lux-arc-card {
        transition: none !important;
    }
}

.lux-arc-card {
    aspect-ratio: 16 / 9;
    border: 1px solid #c9a84c;
    border-radius: 0;
    position: absolute;
    top: 50%;
    overflow: hidden;
    transform-origin: center center;
    // `flat` (not preserve-3d): the card's own rotateY still reads 3D
    // thanks to the parent's perspective, but children render on the
    // card's flat surface. preserve-3d combined with overflow:hidden
    // caused the logo to flatten/vanish at corner rotations.
    transform-style: flat;
    cursor: pointer;
    box-shadow:
        0 8px 32px rgba(0, 0, 0, 0.6),
        0 0 16px rgba(245, 208, 64, 0.06);
    outline: 1px solid rgba(201, 152, 76, 0.35);
    outline-offset: 3px;
    transition:
        left 0.35s cubic-bezier(0.25, 0.1, 0.25, 1),
        transform 0.35s cubic-bezier(0.25, 0.1, 0.25, 1),
        filter 0.3s ease,
        border-color 0.3s ease,
        box-shadow 0.3s ease;

    // Top highlight
    &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(to bottom, rgba(255, 255, 255, 0.04) 0%, transparent 40%);
        border-radius: inherit;
        z-index: 4;
        pointer-events: none;
    }

    // Center card shimmer
    &.arc-pos-3::before {
        content: '';
        position: absolute;
        top: 0;
        left: -75%;
        width: 50%;
        height: 100%;
        background: linear-gradient(120deg, transparent 0%, rgba(255, 255, 255, 0.18) 50%, transparent 100%);
        transform: skewX(-20deg);
        z-index: 5;
        pointer-events: none;
        animation: luxCardShimmer 0.6s ease 2s forwards;
        opacity: 0;
    }

    // Center card: gold glow + thicker frame
    &.arc-pos-3 {
        outline: 1.5px solid rgba(245, 208, 64, 0.5);
        outline-offset: 4px;
        animation: luxCenterGlow 3s ease-in-out infinite;
    }

    // Per-position static fallback (used after fan-in and by reduced-motion)
    &.arc-pos-1 {
        width: 330px;
        left: calc(50% - 640px);
        transform: translateY(-50%) rotateY(55deg) translateX(60px) scale(0.78);
        z-index: 1;
        filter: brightness(0.78);
        border-color: rgba(201, 158, 0, 0.25);
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.6),
            0 0 20px rgba(201, 152, 76, 0.12);
    }
    &.arc-pos-2 {
        width: 350px;
        left: calc(50% - 470px);
        transform: translateY(-50%) rotateY(32deg) translateX(24px) scale(0.88);
        z-index: 3;
        filter: brightness(0.85);
        border-color: rgba(201, 158, 0, 0.45);
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.6),
            0 0 20px rgba(201, 152, 76, 0.12);
    }
    &.arc-pos-3 {
        width: 520px;
        left: calc(50% - 260px);
        transform: translateY(-50%) rotateY(0deg) scale(1);
        z-index: 5;
        filter: brightness(1);
        border: 1.5px solid #f5d040;
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.6),
            0 0 40px rgba(245, 208, 64, 0.18);
    }
    &.arc-pos-4 {
        width: 350px;
        left: calc(50% + 140px);
        transform: translateY(-50%) rotateY(-32deg) translateX(-24px) scale(0.88);
        z-index: 3;
        filter: brightness(0.85);
        border-color: rgba(201, 158, 0, 0.45);
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.6),
            0 0 20px rgba(201, 152, 76, 0.12);
    }
    &.arc-pos-5 {
        width: 330px;
        left: calc(50% + 320px);
        transform: translateY(-50%) rotateY(-55deg) translateX(-60px) scale(0.78);
        z-index: 1;
        filter: brightness(0.78);
        border-color: rgba(201, 158, 0, 0.25);
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.6),
            0 0 20px rgba(201, 152, 76, 0.12);
    }
}

// Hover effects — scoped with :not(.lux-arc-rotating) so the !important hover
// transform doesn't override the JS-driven inline transform mid-rotation. Without
// this, clicking a side card kept it frozen at its old hover-pose for the full
// 800 ms, then snapped to the new hover-pose via a 350 ms CSS transition once
// the arc-pos class swapped.
.lux-card-arc:not(.lux-arc-rotating):hover .lux-arc-card:hover {
    filter: brightness(1.1) !important;
    border-color: #f5d040 !important;
    box-shadow:
        0 12px 48px rgba(0, 0, 0, 0.6),
        0 0 28px rgba(240, 188, 0, 0.25) !important;
    animation: none !important;
}

.lux-card-arc:not(.lux-arc-rotating) .lux-arc-card.arc-pos-1:hover {
    transform: translateY(-50%) rotateY(55deg) translateX(60px) scale(0.88) !important;
}
.lux-card-arc:not(.lux-arc-rotating) .lux-arc-card.arc-pos-2:hover {
    transform: translateY(-50%) rotateY(32deg) translateX(24px) scale(0.98) !important;
}
.lux-card-arc:not(.lux-arc-rotating) .lux-arc-card.arc-pos-3:hover {
    transform: translateY(-50%) rotateY(0deg) scale(1.03) !important;
}
.lux-card-arc:not(.lux-arc-rotating) .lux-arc-card.arc-pos-4:hover {
    transform: translateY(-50%) rotateY(-32deg) translateX(-24px) scale(0.98) !important;
}
.lux-card-arc:not(.lux-arc-rotating) .lux-arc-card.arc-pos-5:hover {
    transform: translateY(-50%) rotateY(-55deg) translateX(-60px) scale(0.88) !important;
}

// Card inner elements
.lux-card-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: inherit;
}

.lux-card-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background:
        radial-gradient(ellipse at top, rgba(255, 255, 255, 0.08), transparent 60%),
        linear-gradient(160deg, var(--lux-card-fallback-bg, #1a3026) 0%, #0a1812 100%);
    text-align: center;
}

.lux-card-fallback-name {
    color: rgba(255, 255, 255, 0.92);
    font-family: 'Jost', sans-serif;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 0.04em;
    line-height: 1.2;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);

    @media (min-width: 640px) {
        font-size: 16px;
    }
}

.lux-card-video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    aspect-ratio: unset;
    border-radius: inherit;
}

.lux-card-vignette {
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, transparent 40%, rgba(4, 10, 6, 0.55) 75%, rgba(4, 10, 6, 0.85) 100%);
    z-index: 1;
    border-radius: inherit;
}

.lux-card-logo {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 60%;
    max-width: 160px;
    // Override the HTML `height` attribute NuxtImg writes; without this the
    // image is forced to a fixed pixel height and stretches non-2:1 logos.
    height: auto;
    z-index: 2;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.8));
    pointer-events: none;
    transition:
        opacity 0.4s ease,
        top 0.3s ease;
}

.lux-card-cta {
    position: absolute;
    bottom: 14px;
    left: 50%;
    transform: translateX(-50%) translateY(4px);
    background: linear-gradient(#ffe301, #ffb901);
    color: #0e3731;
    font-family: 'Jost', sans-serif;
    font-weight: 700;
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    padding: 6px 18px;
    border-radius: 6px;
    z-index: 3;
    opacity: 0;
    transition:
        opacity 0.2s ease,
        transform 0.2s ease;
    pointer-events: none;
}

// Center card WITH video: shrink logo into the top-left so the video
// can play unobscured. Without a video, the rule below doesn't apply and
// the logo stays in its default centered position.
.lux-arc-card--center-has-video.arc-pos-3 .lux-card-logo {
    opacity: 1;
    top: 14px;
    left: 14px;
    transform: none;
    width: 26%;
    max-width: 84px;
    transition:
        opacity 0.3s ease,
        top 0.3s ease,
        left 0.3s ease,
        width 0.3s ease;
}
.lux-arc-card--center-has-video.arc-pos-3:hover .lux-card-logo {
    top: 14px;
}

.lux-card-start {
    position: absolute;
    bottom: 14px;
    // Default = no video: button sits centered under the (centered) logo.
    left: 50%;
    transform: translateX(-50%);
    z-index: 3;
    background: linear-gradient(135deg, #f5d040, #f0bc00, #c99e00);
    color: #0d1a0f;
    border: none;
    padding: 8px 18px;
    font-family: 'Jost', sans-serif;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
    transition:
        filter 0.2s ease,
        box-shadow 0.2s ease,
        transform 0.2s ease;

    &:hover {
        filter: brightness(1.08);
        box-shadow:
            0 6px 18px rgba(0, 0, 0, 0.5),
            0 0 12px rgba(245, 208, 64, 0.35);
    }

    &:active {
        transform: translateX(-50%) translateY(1px);
    }
}

// Start button fade-in/out transition
// enter: 300ms ease-in, slightly delayed so it lands after the card
// reaches center. exit: 200ms quick fade so it doesn't linger visually.
.lux-btn-fade-enter-active {
    transition: opacity 300ms ease 120ms;
}

.lux-btn-fade-leave-active {
    transition: opacity 200ms ease;
}

.lux-btn-fade-enter-from,
.lux-btn-fade-leave-to {
    opacity: 0;
}

// With a hero video, the logo is shrunk into the top-left so reposition
// the start button to the bottom-right corner to balance the card.
.lux-arc-card--center-has-video .lux-card-start {
    left: auto;
    right: 14px;
    transform: none;

    &:active {
        transform: translateY(1px);
    }
}

// Keyframe animations
@keyframes luxFadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes luxSlideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes luxDrawLine {
    from {
        transform: scaleX(0);
    }
    to {
        transform: scaleX(1);
    }
}

@keyframes luxCenterGlow {
    0%,
    100% {
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.6),
            0 0 24px rgba(245, 208, 64, 0.12);
    }
    50% {
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.6),
            0 0 40px rgba(245, 208, 64, 0.22);
    }
}

@keyframes luxCardShimmer {
    0% {
        left: -75%;
        opacity: 1;
    }
    100% {
        left: 130%;
        opacity: 1;
    }
}

@keyframes luxConfettiFall {
    0% {
        opacity: 0;
        transform: translateY(0) rotate(0deg);
    }
    10% {
        opacity: 0.5;
    }
    90% {
        opacity: 0.25;
    }
    100% {
        opacity: 0;
        transform: translateY(110vh) rotate(720deg);
    }
}

@media (max-width: 767px) {
    .lux-hero {
        padding-top: 0;
    }
}

// Mobile: scaled-down arc
@media (max-width: 639px) {
    .lux-hero::after {
        background-image: url('/media/landing-page/lux/lux-hero-mobile.png');
        background-size: cover;
        background-position: center top;
    }

    .hero-inner {
        padding: 100px 16px 0;
    }

    .lux-hero-rule {
        margin-bottom: 10px;
    }

    .lux-hero-label {
        font-size: 12px;
        letter-spacing: 0.3em;
        margin-bottom: 8px;
    }

    h1 {
        font-size: 39px;
        margin-bottom: 10px;
        line-height: 1.15;
    }

    .lux-hero-subcopy {
        font-size: 15px;
        margin-bottom: 40px;
    }

    .lux-btn-cta {
        padding: 17px 72px;
        font-size: 1.3rem;
    }

    .lux-hero-trust {
        margin-top: 8px;
        font-size: 13px;
    }

    .lux-arc-divider {
        width: 60%;
        margin-top: 16px;
        margin-bottom: 4px;
    }

    .lux-card-arc {
        height: 228px;
        padding: 0;
        perspective: 750px;
        overflow: hidden;

        &::before {
            background:
                linear-gradient(to right, #0d2018 0%, transparent 12%, transparent 88%, #0d2018 100%),
                linear-gradient(to bottom, transparent 0%, rgba(10, 26, 18, 0.4) 92%, #0a1a12 100%);
        }
    }

    .lux-arc-card {
        aspect-ratio: 16 / 9;

        &.arc-pos-1 {
            width: 190px;
            left: calc(50% - 270px);
            transform: translateY(-50%) rotateY(55deg) translateX(31px) scale(0.78);
            filter: brightness(0.88);
        }
        &.arc-pos-2 {
            width: 205px;
            left: calc(50% - 190px);
            transform: translateY(-50%) rotateY(32deg) translateX(12px) scale(0.88);
            filter: brightness(0.92);
        }
        &.arc-pos-3 {
            width: 300px;
            left: calc(50% - 150px);
            transform: translateY(-50%) rotateY(0deg) scale(1);
            filter: brightness(1);
            box-shadow:
                0 8px 40px rgba(0, 0, 0, 0.5),
                0 0 20px rgba(201, 158, 0, 0.12);
        }
        &.arc-pos-4 {
            width: 205px;
            left: calc(50% - 10px);
            transform: translateY(-50%) rotateY(-32deg) translateX(-12px) scale(0.88);
            filter: brightness(0.92);
        }
        &.arc-pos-5 {
            width: 190px;
            left: calc(50% + 85px);
            transform: translateY(-50%) rotateY(-55deg) translateX(-31px) scale(0.78);
            filter: brightness(0.88);
        }
    }

    .lux-card-arc:not(.lux-arc-rotating) .lux-arc-card.arc-pos-1:hover {
        transform: translateY(-50%) rotateY(55deg) translateX(31px) scale(0.88) !important;
    }
    .lux-card-arc:not(.lux-arc-rotating) .lux-arc-card.arc-pos-2:hover {
        transform: translateY(-50%) rotateY(32deg) translateX(12px) scale(0.98) !important;
    }
    .lux-card-arc:not(.lux-arc-rotating) .lux-arc-card.arc-pos-3:hover {
        transform: translateY(-50%) rotateY(0deg) scale(1.03) !important;
    }
    .lux-card-arc:not(.lux-arc-rotating) .lux-arc-card.arc-pos-4:hover {
        transform: translateY(-50%) rotateY(-32deg) translateX(-12px) scale(0.98) !important;
    }
    .lux-card-arc:not(.lux-arc-rotating) .lux-arc-card.arc-pos-5:hover {
        transform: translateY(-50%) rotateY(-55deg) translateX(-31px) scale(0.88) !important;
    }

    .lux-card-logo {
        width: 65%;
        max-width: 100px;
    }

    .lux-arc-card--center-has-video.arc-pos-3 .lux-card-logo {
        top: 10px;
        left: 10px;
        width: 28%;
        max-width: 60px;
    }

    .lux-card-start {
        bottom: 10px;
        padding: 6px 12px;
        font-size: 10px;
    }

    .lux-arc-card--center-has-video .lux-card-start {
        right: 10px;
    }

    .lux-card-cta {
        font-size: 9px;
        padding: 4px 12px;
        bottom: 10px;
    }
}

@media (max-width: 450px) {
    .lux-btn-cta {
        padding: 17px;
        font-size: 1.1rem;
        width: 100%;
    }
}

// Reduced motion
@media (prefers-reduced-motion: reduce) {
    // Surrounding intro elements: skip the entrance animations, show
    // them at their final state.
    .lux-hero-rule,
    .lux-hero-label,
    h1,
    .lux-hero-subcopy,
    .lux-btn-cta,
    .lux-hero-trust,
    .lux-arc-divider {
        opacity: 1 !important;
        transform: none !important;
        animation: none !important;
    }

    // Arc cards keep their static arc layout (driven by the `arc-pos-*`
    // classes that JS applies on mount). Only override the JS-set
    // initial opacity:0 and disable looping animations like luxCenterGlow
    // and luxCardShimmer — don't touch position/transform, or the cards
    // collapse out of the arc.
    .lux-arc-card {
        opacity: 1 !important;
        animation: none !important;

        &::before,
        &::after {
            animation: none !important;
        }
    }

    .lux-confetti i {
        animation: none !important;
        display: none;
    }
}
</style>
